/**
 * usePasswordRecoveryRoute — ensure Supabase password-recovery deep links land
 * on the Login page so the PASSWORD_RECOVERY listener in Login.tsx fires and
 * the recovery UI is shown.
 *
 * Background: Supabase's implicit-grant flow appends auth params to the URL
 * fragment after the email-link verify step:
 *   https://aip.mi6group.com/#access_token=...&refresh_token=...&type=recovery&...
 *
 * Because wouter uses hash-based routing (useHashLocation), the route lives
 * inside the same fragment. Supabase's append clobbers whatever route was
 * there before, so the SPA renders the root route (/) instead of /login.
 * The PASSWORD_RECOVERY useEffect inside Login.tsx is scoped to that page,
 * so it never fires — the user is stranded on the home page with a recovery
 * session quietly established in the background.
 *
 * Fix: sniff the hash on mount for `type=recovery`. If present, splice in a
 * `/login` route segment while preserving every Supabase auth param so the
 * Supabase JS client can still complete the recovery handshake on its next
 * tick. Wouter's hash-location reader will see `/login` and mount the page;
 * Login.tsx's onAuthStateChange listener picks up PASSWORD_RECOVERY normally.
 *
 * Hash forms handled:
 *   #access_token=...&type=recovery&...           → #/login?reset=1&access_token=...&type=recovery
 *   #/some/route#access_token=...&type=recovery   → #/login?reset=1&access_token=...&type=recovery
 *   #/&access_token=...&type=recovery              → #/login?reset=1&access_token=...&type=recovery
 */
import { useEffect } from "react";

/**
 * Wouter's hash router treats EVERYTHING after `#` as the route path —
 * including query strings. So `#/login?reset=1` matches no route and
 * renders a 404. We therefore route to plain `#/login` with no query
 * string, and stash the recovery signal in sessionStorage where Login.tsx
 * can read it. Supabase JS has already consumed the access_token from
 * window.location.hash during createClient() (detectSessionInUrl=true),
 * so the tokens do not need to survive in the URL.
 */
export function usePasswordRecoveryRoute(): void {
  useEffect(() => {
    const raw = window.location.hash || "";
    if (!raw) return;
    const lower = raw.toLowerCase();
    if (!lower.includes("type=recovery")) return;

    // Mark that this load is a recovery flow so Login.tsx can show recovery
    // UI even if PASSWORD_RECOVERY fires before the listener mounts.
    try { sessionStorage.setItem("aip:passwordRecovery", "1"); } catch {}

    // If we are already on /login (or /pilot/login), still strip the query
    // string because wouter cannot match it.
    const target = lower.startsWith("#/pilot/login") ? "#/pilot/login" : "#/login";

    try {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search + target,
      );
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } catch {
      window.location.hash = target;
    }
  }, []);
}
